'use client';

import {
  Alert,
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import { ConnectorType } from '@repo/api-gw-sdk';
import { useState } from 'react';

import { FormTextField } from '@/components/form/formTextField';
import type { FieldValidation } from '@/components/form/validation/fieldValidation';
import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';

import type { AddCatalogWizardState } from '../addCatalogWizardState';

const awsAccountIdValidation: FieldValidation = {
  validateInput: (value: string) => /^\d{12}$/.test(value),
  errorMessage: () => 'AWS Account ID must be exactly 12 digits',
};

export const InlineAddConnectorStep = (
  props: StepProps<AddCatalogWizardState>
) => {
  const dal = useDAL();
  const { workspaceId } = props.wizardState;
  const [acknowledged, setAcknowledged] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const connectorState =
    props.wizardState.connectorSource?.mode === 'new'
      ? props.wizardState.connectorSource.connectorState
      : null;

  if (!connectorState || !workspaceId) return null;

  const { awsAccountId, discoveryRoleName, dataAccessRoleName, connectorName } =
    connectorState;

  const updateConnectorState = (updates: Partial<typeof connectorState>) => {
    if (props.wizardState.connectorSource?.mode !== 'new') return;
    props.setWizardState({
      ...props.wizardState,
      connectorSource: {
        ...props.wizardState.connectorSource,
        connectorState: { ...connectorState, ...updates },
      },
    });
  };

  const isAccountIdValid = /^\d{12}$/.test(awsAccountId);
  const isMissingFields =
    !awsAccountId ||
    !discoveryRoleName ||
    !dataAccessRoleName ||
    !connectorName.trim();

  const canCreate =
    !isMissingFields && isAccountIdValid && acknowledged && !creating;

  const handleCreate = async () => {
    setError(null);
    setCreating(true);
    try {
      const result = await dal.lakehouse.createConnector(workspaceId, {
        name: connectorName,
        connectorType: ConnectorType.CONNECTOR_TYPE_AWS_ACCOUNT_CONNECTOR,
        awsAccountProperties: {
          awsAccountId,
          discoveryRoleName,
          dataAccessRoleName,
        },
      });
      props.setWizardState({
        ...props.wizardState,
        connectorSource: {
          mode: 'new',
          connectorState,
          createdConnectorId: result.id,
        },
        connectorExternalId: awsAccountId,
      });
      props.setNextStep(props.currentStep.next?.[0]);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to create connector'
      );
    } finally {
      setCreating(false);
    }
  };

  return (
    <StepContainer
      stepperIndex={1}
      stepperLabels={props.stepperLabels}
      canGoNext={() => canCreate}
      nextButtonText={creating ? 'Creating...' : 'Create Connector & Continue'}
      nextButtonLoadingText='Creating...'
      backButtonText='Back'
      onBackClick={props.back}
      onNextClick={handleCreate}
      sx={{ overflow: 'auto', paddingX: '0px', paddingY: 'min(60px, 4vw)' }}
    >
      <Stack gap='min(60px, 4vw)' divider={<Divider />}>
        {/* Connector Details */}
        <Box paddingX='min(100px, 6vw)'>
          <Stack gap='24px' maxWidth='540px'>
            <Stack gap='8px'>
              <Typography variant='h6'>Connector Details</Typography>
              <Typography variant='body2' color='text.secondary'>
                Name the connector and specify the AWS account it connects to.
              </Typography>
            </Stack>

            <FormTextField
              title='Connector Name'
              size='small'
              placeholder='e.g., AWS Account 123456789012'
              value={connectorName}
              onChange={(e) =>
                updateConnectorState({ connectorName: e.target.value })
              }
            />

            <FormTextField
              title='AWS Account ID'
              size='small'
              placeholder='123456789012'
              value={awsAccountId}
              validation={awsAccountIdValidation}
              onChange={(e) => {
                const newAccountId = e.target.value
                  .replace(/\D/g, '')
                  .slice(0, 12);
                const updates: Partial<typeof connectorState> = {
                  awsAccountId: newAccountId,
                };
                const currentDefault = `AWS Account ${awsAccountId}`;
                if (!connectorName || connectorName === currentDefault) {
                  updates.connectorName = newAccountId
                    ? `AWS Account ${newAccountId}`
                    : '';
                }
                updateConnectorState(updates);
              }}
            />
          </Stack>
        </Box>

        {/* IAM Roles */}
        <Box paddingX='min(100px, 6vw)'>
          <Stack gap='24px' maxWidth='540px'>
            <Stack gap='8px'>
              <Typography variant='h6'>IAM Roles</Typography>
              <Typography variant='body2' color='text.secondary'>
                Specify the IAM role names that Eon will assume in your AWS
                account for discovery and data access.
              </Typography>
            </Stack>

            <FormTextField
              title='Discovery Role Name'
              size='small'
              placeholder='e.g., EonDiscoveryRole'
              value={discoveryRoleName}
              onChange={(e) =>
                updateConnectorState({ discoveryRoleName: e.target.value })
              }
            />

            <FormTextField
              title='Data Access Role Name'
              size='small'
              placeholder='e.g., EonDataAccessRole'
              value={dataAccessRoleName}
              onChange={(e) =>
                updateConnectorState({ dataAccessRoleName: e.target.value })
              }
            />
          </Stack>
        </Box>

        {/* Deployment Confirmation */}
        <Box paddingX='min(100px, 6vw)'>
          <Stack gap='24px' maxWidth='540px'>
            <Stack gap='8px'>
              <Typography variant='h6'>Deployment</Typography>
              <Typography variant='body2' color='text.secondary'>
                Confirm that the required IAM roles have been deployed in your
                AWS account before creating the connector.
              </Typography>
            </Stack>

            {awsAccountId && (
              <Alert severity='info'>
                <Typography variant='body2'>
                  Ensure the following IAM roles are deployed in AWS account{' '}
                  <strong>{awsAccountId}</strong>:
                </Typography>
                <Typography
                  variant='body2'
                  component='ul'
                  sx={{ mt: '8px', pl: '16px' }}
                >
                  <li>
                    <code>
                      arn:aws:iam::{awsAccountId}:role/{discoveryRoleName}
                    </code>
                  </li>
                  <li>
                    <code>
                      arn:aws:iam::{awsAccountId}:role/{dataAccessRoleName}
                    </code>
                  </li>
                </Typography>
                <Typography variant='body2' sx={{ mt: '8px' }}>
                  These roles must have a trust policy allowing Eon to assume
                  them.
                </Typography>
              </Alert>
            )}

            <FormControlLabel
              control={
                <Checkbox
                  checked={acknowledged}
                  onChange={(e) => setAcknowledged(e.target.checked)}
                  size='small'
                />
              }
              label={
                <Typography variant='body2'>
                  I have deployed the IAM roles in my AWS account
                </Typography>
              }
            />
          </Stack>
        </Box>
      </Stack>

      {error && (
        <Box paddingX='min(100px, 6vw)' paddingTop='24px'>
          <Alert severity='error' sx={{ maxWidth: '540px' }}>
            <Typography variant='body2'>{error}</Typography>
          </Alert>
        </Box>
      )}
    </StepContainer>
  );
};
